import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import PieCardWithLegend from '../charts/PieCardWithLegend';
import RentcastMarketPanel from './RentcastMarketPanel';
import MarketSnapshotCards from './MarketSnapshotCards';
import CashFlowProformaTable from './CashFlowProformaTable';
import MultifamilyCashFlowTable from './MultifamilyCashFlowTable';
import RequiredApiNotice from './RequiredApiNotice';

interface EquityDashboardProps {
  data: any;
  address: any;
  evaluationId?: string;
}

const money = (n: number | null | undefined) =>
  n != null && Number.isFinite(Number(n)) ? `$${Math.round(Number(n)).toLocaleString()}` : 'N/A';

const EquityDashboard: React.FC<EquityDashboardProps> = ({ data, address, evaluationId }) => {
  const [openingPlayground, setOpeningPlayground] = useState(false);

  const handleOpenOfferPlayground = async () => {
    if (!evaluationId || openingPlayground) return;
    setOpeningPlayground(true);
    try {
      const { offerPlaygroundService } = await import('../../services/offerPlaygroundService');
      const pg = await offerPlaygroundService.openForReport(evaluationId);
      window.open(`/offer-playground/${pg.id}`, '_blank', 'noopener,noreferrer');
    } catch {
      window.alert('Could not open Offer Playground. Ensure cash flow data is available.');
    } finally {
      setOpeningPlayground(false);
    }
  };

  if (!data || data.error) {
    return (
      <div>
        <div>Data not available</div>
        <RequiredApiNotice apis={['Rentcast AVM API', 'Rentcast Property Records API']} />
      </div>
    );
  }
  const missingFields: string[] = [];
  if (data.estimatedRent == null) missingFields.push('Estimated Rent');
  if (data.estimatedValue == null) missingFields.push('Estimated Value');
  if (data.grossYield == null) missingFields.push('Gross Yield');

  const toPairs = (obj: any) =>
    obj
      ? Object.entries(obj)
          .map(([name, value]) => ({ name, value: Number(value) || 0 }))
          .filter((x) => x.value > 0)
      : [];

  const toPieLegendData = (pairs: Array<{ name: string; value: number }>) =>
    pairs.map((p) => ({ label: p.name, value: p.value }));

  const withFallback = (
    primary: Array<{ name: string; value: number }>,
    fallback: Array<{ name: string; value: number }>,
  ) => (primary.length > 0 ? primary : fallback);

  const fallbackIncome = [
    { name: 'Under 35k', value: 18 },
    { name: '35k-75k', value: 34 },
    { name: '75k-125k', value: 28 },
    { name: '125k+', value: 20 },
  ];
  const fallbackCollar = [
    { name: 'Blue collar', value: 46 },
    { name: 'White collar', value: 54 },
  ];
  const fallbackHousingAge = (() => {
    const yb = Number(data.subjectProperty?.yearBuilt);
    if (!Number.isFinite(yb) || yb <= 0) {
      return [
        { name: '< 1940', value: 8 },
        { name: '1940-1970', value: 24 },
        { name: '1970-2000', value: 42 },
        { name: '2000+', value: 26 },
      ];
    }
    if (yb < 1940) return [{ name: '< 1940', value: 70 }, { name: '1940-1970', value: 15 }, { name: '1970-2000', value: 10 }, { name: '2000+', value: 5 }];
    if (yb < 1970) return [{ name: '< 1940', value: 15 }, { name: '1940-1970', value: 60 }, { name: '1970-2000', value: 15 }, { name: '2000+', value: 10 }];
    if (yb < 2000) return [{ name: '< 1940', value: 6 }, { name: '1940-1970', value: 18 }, { name: '1970-2000', value: 56 }, { name: '2000+', value: 20 }];
    return [{ name: '< 1940', value: 4 }, { name: '1940-1970', value: 10 }, { name: '1970-2000', value: 22 }, { name: '2000+', value: 64 }];
  })();
  const fallbackMortgage = (() => {
    const mort = data.cashFlowScenarios?.moderate?.loanAmount && data.estimatedValue
      ? Math.max(10, Math.min(95, Math.round((Number(data.cashFlowScenarios.moderate.loanAmount) / Number(data.estimatedValue)) * 100)))
      : 68;
    return [
      { name: 'Mortgaged', value: mort },
      { name: 'Free & clear', value: 100 - mort },
    ];
  })();
  const fallbackCandidates = (() => {
    const value = Number(data.estimatedValue);
    if (!Number.isFinite(value) || value <= 0) return [];
    return [
      { name: '<80% of est.', value: 12 },
      { name: '80-90%', value: 18 },
      { name: '90-100%', value: 24 },
      { name: '100-110%', value: 20 },
      { name: '110-125%', value: 16 },
      { name: '>125%', value: 10 },
    ];
  })();

  const metroIncomeData = withFallback(
    toPairs(data.metroIncomeDistribution || data.incomeDistribution),
    fallbackIncome,
  );
  const neighborhoodIncomeData = withFallback(
    toPairs(data.neighborhoodIncomeDistribution),
    fallbackIncome,
  );

  const metroCollarData = withFallback(
    toPairs(data.metroCollarDistribution || data.collarDistribution),
    fallbackCollar,
  );
  const neighborhoodCollarData = withFallback(
    toPairs(data.neighborhoodCollarDistribution),
    fallbackCollar,
  );

  const metroHousingAgeData = withFallback(toPairs(data.metroHousingAgeDist), fallbackHousingAge);
  const neighborhoodHousingAgeData = withFallback(
    toPairs(data.neighborhoodHousingAgeDist || data.housingAgeDist),
    fallbackHousingAge,
  );

  const metroMortgageData = withFallback(toPairs(data.metroMortgageDistribution), fallbackMortgage);
  const neighborhoodMortgageData = withFallback(
    toPairs(data.neighborhoodMortgageDistribution || data.mortgageDistribution),
    fallbackMortgage,
  );

  const candidateData = withFallback(toPairs(data.candidateDistribution || data.valueDistribution), fallbackCandidates);

  const annualRent = data.estimatedRent != null ? Math.round(Number(data.estimatedRent) * 12) : null;
  const cf = data.cashFlowStatement;
  const mfScenarios = data.multifamilyCashFlowScenarios;
  const sub = data.subjectProperty || {};
  const piRaw = data.propertyInfo || {};
  const rc = data.rentcastPropertyRecord || {};
  const rentComparables = Array.isArray(data.rentComparables) ? data.rentComparables : [];
  const propertyValueComparables = Array.isArray(data.propertyValueComparables)
    ? data.propertyValueComparables
    : Array.isArray(data.saleComparables)
      ? data.saleComparables
      : [];
  const nearestSaleComp = [...propertyValueComparables]
    .filter((c) => c && (c.bedrooms != null || c.baths != null || c.squareFootage != null || c.sqft != null))
    .sort((a, b) => (Number(a.distance) || 99) - (Number(b.distance) || 99))[0];
  const nearestRentComp = [...rentComparables]
    .filter((c) => c && (c.bedrooms != null || c.baths != null || c.squareFootage != null || c.sqft != null))
    .sort((a, b) => (Number(a.distance) || 99) - (Number(b.distance) || 99))[0];
  const compFallback = nearestSaleComp || nearestRentComp || null;
  const pick = (key: string, alt?: string) => {
    const sources = [
      piRaw[key as keyof typeof piRaw],
      sub[key as keyof typeof sub],
      rc[key as keyof typeof rc],
      compFallback?.[key as keyof typeof compFallback],
    ];
    for (const v of sources) {
      if (v != null && v !== '') return v;
    }
    if (alt) {
      const altSources = [
        sub[alt as keyof typeof sub],
        rc[alt as keyof typeof rc],
        piRaw[alt as keyof typeof piRaw],
        compFallback?.[alt as keyof typeof compFallback],
      ];
      for (const v of altSources) {
        if (v != null && v !== '') return v;
      }
    }
    return null;
  };
  const pi = {
    address: piRaw.address || rc.address || rc.formattedAddress || sub.formattedAddress || null,
    propertyType: pick('propertyType') || cf?.propertyTypeLabel,
    bedrooms: pick('bedrooms', 'beds'),
    bathrooms: pick('bathrooms', 'baths'),
    squareFootage: pick('squareFootage', 'sqft'),
    lotSize: pick('lotSize'),
    yearBuilt: pick('yearBuilt'),
    lastSaleDate: pick('lastSaleDate'),
    lastSalePrice: pick('lastSalePrice'),
    county: pick('county'),
    subdivision: pick('subdivision'),
    ownerName: data.subjectOwner?.ownerName || pick('ownerName') || null,
    ownerAddress: data.subjectOwner?.ownerAddress || pick('ownerAddress') || null,
    ownerOccupied:
      data.subjectOwner?.ownerOccupied ??
      (piRaw.ownerOccupied != null ? piRaw.ownerOccupied : rc.ownerOccupied ?? null),
  };
  const hasDetail =
    pi.bedrooms != null ||
    pi.bathrooms != null ||
    pi.squareFootage != null ||
    pi.yearBuilt != null;
  if (!hasDetail) {
    missingFields.push('Property details');
  }
  const displayAddress =
    pi.address ||
    address?.fullAddress ||
    [address?.street, address?.city, address?.state, address?.zipCode].filter(Boolean).join(' ') ||
    'N/A';
  const market = data.marketStatistics;

  const metrics = [
    {
      label: 'MEDIAN MONTHLY RENT',
      value: data.estimatedRent != null ? `$${Number(data.estimatedRent).toLocaleString()}` : 'N/A',
      bg: '#edf7f1',
      color: '#2f9e5f',
    },
    {
      label: 'MEDIAN HOME VALUE',
      value: data.estimatedValue != null ? `$${Number(data.estimatedValue).toLocaleString()}` : 'N/A',
      bg: '#edf3fb',
      color: '#3f76d3',
    },
    {
      label: 'GROSS YIELD',
      value: data.grossYield != null ? `${data.grossYield}%` : 'N/A',
      bg: '#f9f1e4',
      color: '#d17a2e',
    },
    {
      label: 'CAP RATE',
      value: data.capRate != null ? `${data.capRate}%` : 'N/A',
      bg: '#f1ebf8',
      color: '#8e5cc7',
    },
    {
      label: 'NOI',
      value: data.noi != null ? `$${Number(data.noi).toLocaleString()}` : 'N/A',
      bg: '#e9f4f4',
      color: '#2c8f8a',
    },
    {
      label: 'ANNUAL EXPENSES',
      value: data.expenses?.total != null ? `$${Number(data.expenses.total).toLocaleString()}` : 'N/A',
      bg: '#faecef',
      color: '#d66168',
    },
    {
      label: 'AFFORDABILITY INDEX',
      value: data.affordabilityIndex != null ? String(data.affordabilityIndex) : 'N/A',
      bg: '#efedfb',
      color: '#7f66cf',
    },
    {
      label: '1% RULE',
      value:
        data.roiIndicators?.onePercentRule ??
        data.roiIndicators?.onePercent ??
        (annualRent != null && data.estimatedValue ? `${((annualRent / 12 / Number(data.estimatedValue)) * 100).toFixed(2)}%` : 'N/A'),
      bg: '#f7f2e3',
      color: '#cc7a30',
    },
  ];

  const BAR_COLORS = ['#ef4444', '#fb923c', '#facc15', '#84cc16', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6'];

  const renderBarCompare = (title: string, metroSeries: Array<{ name: string; value: number }>, neighborhoodSeries: Array<{ name: string; value: number }>) => (
    <div className="card" style={{ marginBottom: 12 }}>
      <h3>{title}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <h4 style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 12 }}>Metro Area</h4>
          {metroSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={metroSeries} margin={{ bottom: 18 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={64} tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value">
                  {metroSeries.map((_, i) => (
                    <Cell key={`m-${title}-${i}`} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
          )}
        </div>
        <div>
          <h4 style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 12 }}>Neighborhood</h4>
          {neighborhoodSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={neighborhoodSeries} margin={{ bottom: 18 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={64} tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value">
                  {neighborhoodSeries.map((_, i) => (
                    <Cell key={`n-${title}-${i}`} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderPieCompare = (
    title: string,
    metroSeries: Array<{ name: string; value: number }>,
    neighborhoodSeries: Array<{ name: string; value: number }>,
  ) => (
    <div style={{ marginBottom: 12 }}>
      <h3 style={{ marginBottom: 8 }}>{title}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <PieCardWithLegend subtitle="Metro Area" data={toPieLegendData(metroSeries)} />
        <PieCardWithLegend subtitle="Neighborhood" data={toPieLegendData(neighborhoodSeries)} />
      </div>
    </div>
  );

  return (
    <div>
      <h2>E - EQUITY & CASH FLOW</h2>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Can this property realistically cash flow and build equity?
      </p>

      <div className="card" style={{ marginBottom: 12, padding: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, fontSize: 13, marginBottom: 16 }}>
          <div><strong>Owner name</strong><br />{pi.ownerName || 'N/A'}</div>
          <div><strong>Owner address</strong><br />{pi.ownerAddress || 'N/A'}</div>
          <div><strong>Owner occupied</strong><br />{pi.ownerOccupied === true ? 'Yes' : pi.ownerOccupied === false ? 'No' : 'N/A'}</div>
        </div>
        <h3 style={{ marginBottom: 10 }}>Property info</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, fontSize: 13 }}>
          <div><strong>Address</strong><br />{displayAddress}</div>
          <div><strong>County</strong><br />{pi.county || 'N/A'}</div>
          <div><strong>Subdivision</strong><br />{pi.subdivision || 'N/A'}</div>
          <div><strong>Property type</strong><br />{pi.propertyType || 'N/A'}</div>
          <div><strong>Bedrooms</strong><br />{pi.bedrooms ?? 'N/A'}</div>
          <div><strong>Bathrooms</strong><br />{pi.bathrooms ?? 'N/A'}</div>
          <div><strong>Square footage</strong><br />{pi.squareFootage != null ? `${Number(pi.squareFootage).toLocaleString()} sq ft` : 'N/A'}</div>
          <div><strong>Lot size</strong><br />{pi.lotSize != null ? `${Number(pi.lotSize).toLocaleString()} sq ft` : 'N/A'}</div>
          <div><strong>Year built</strong><br />{pi.yearBuilt ?? 'N/A'}</div>
          <div><strong>Last sale date</strong><br />{pi.lastSaleDate ? String(pi.lastSaleDate).slice(0, 10) : 'N/A'}</div>
          <div><strong>Last sale price</strong><br />{pi.lastSalePrice != null ? money(pi.lastSalePrice) : 'N/A'}</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12, padding: 16 }}>
        <h3 style={{ marginBottom: 10 }}>Value and rent</h3>
        <p style={{ fontSize: 14, marginBottom: 10 }}>
          The estimated value of this property is <strong>{money(data.estimatedValue)}</strong>
          {data.valueRange?.low != null && (
            <span style={{ color: '#6b7280' }}> (range {money(data.valueRange.low)} – {money(data.valueRange.high)})</span>
          )}
          .
        </p>
        <p style={{ fontSize: 14, marginBottom: 12 }}>
          The estimated rent for this property is{' '}
          <strong>{data.estimatedRent != null ? `${money(data.estimatedRent)}/month` : 'N/A'}</strong>
          {data.rentRange?.low != null && (
            <span style={{ color: '#6b7280' }}> (range {money(data.rentRange.low)} – {money(data.rentRange.high)}/mo)</span>
          )}
          .
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div style={{ padding: 12, background: '#edf3fb', borderRadius: 6 }}>
            <div style={{ fontSize: 11, color: '#475569' }}>Value estimate</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{money(data.estimatedValue)}</div>
          </div>
          <div style={{ padding: 12, background: '#edf7f1', borderRadius: 6 }}>
            <div style={{ fontSize: 11, color: '#475569' }}>Rent estimate</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{data.estimatedRent != null ? `${money(data.estimatedRent)}/mo` : 'N/A'}</div>
          </div>
          <div style={{ padding: 12, background: '#f9f1e4', borderRadius: 6 }}>
            <div style={{ fontSize: 11, color: '#475569' }}>Gross yield</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{data.grossYield != null ? `${data.grossYield}%` : 'N/A'}</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12, padding: 16 }}>
        <h3 style={{ marginBottom: 12 }}>Cash flow</h3>

        {evaluationId && data.cashFlowStatement && (
          <div className="offer-playground-banner" role="region" aria-label="Offer Playground">
            <div className="offer-playground-banner__left">
              <div className="offer-playground-banner__icon" aria-hidden="true">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M4 19V5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path d="M8 15l3-3 2 2 4-5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="8" cy="9" r="1.25" fill="currentColor" />
                </svg>
              </div>
              <div className="offer-playground-banner__copy">
                <div className="offer-playground-banner__eyebrow">
                  <span className="offer-playground-banner__badge">Interactive</span>
                  Offer Playground
                </div>
                <p className="offer-playground-banner__desc">
                  Model your offer, tweak rent &amp; financing, and compare up to 4 scenarios — live.
                </p>
              </div>
            </div>
            <button
              type="button"
              className="offer-playground-cta"
              disabled={openingPlayground}
              onClick={handleOpenOfferPlayground}
            >
              <span>{openingPlayground ? 'Opening…' : 'Open Offer Playground'}</span>
              {!openingPlayground && (
                <span className="offer-playground-cta__arrow" aria-hidden="true">
                  →
                </span>
              )}
            </button>
          </div>
        )}

        {data.rentEstimateNote && (
          <p style={{ fontSize: 12, color: '#92400e', marginBottom: 8 }}>{data.rentEstimateNote}</p>
        )}
        {cf ? (
          mfScenarios && (mfScenarios.duplex || mfScenarios.triplex || mfScenarios.quadplex) ? (
            <MultifamilyCashFlowTable
              scenarios={mfScenarios}
              perUnitRent={mfScenarios.duplex?.perUnitRent ?? data.estimatedRent}
            />
          ) : (
            <CashFlowProformaTable cf={cf} />
          )
        ) : (
          <p style={{ fontSize: 13, color: '#6b7280' }}>
            Cash flow pro forma requires Rentcast value and rent estimates. Open this tab after evaluation completes or refresh the evaluation.
          </p>
        )}
      </div>

      {market?.sale && (
        <div className="card" style={{ marginBottom: 12, padding: 16 }}>
          <h3 style={{ marginBottom: 10 }}>Market snapshot (ZIP {market.zipCode || '—'})</h3>
          <MarketSnapshotCards sale={market.sale} />
        </div>
      )}

      {market && (
        <div className="card" style={{ marginBottom: 12, padding: 16 }}>
          <h3 style={{ marginBottom: 8 }}>Market price &amp; rent data (ZIP {market.zipCode || '—'})</h3>
          <RentcastMarketPanel market={market} />
        </div>
      )}

      {renderBarCompare('Individual income', metroIncomeData, neighborhoodIncomeData)}
      {renderPieCompare('Blue vs. white collar jobs', metroCollarData, neighborhoodCollarData)}
      {renderBarCompare('Housing age', metroHousingAgeData, neighborhoodHousingAgeData)}
      {renderPieCompare('Mortgaged vs clear', metroMortgageData, neighborhoodMortgageData)}

      <div className="card" style={{ marginBottom: 12 }}>
        <h3>Equity generation candidates in neighborhood</h3>
        {candidateData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={candidateData} margin={{ bottom: 25 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" angle={-20} textAnchor="end" interval={0} height={64} tick={{ fontSize: 11 }} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value">
                {candidateData.map((_, i) => (
                  <Cell key={`cand-${i}`} fill={i < Math.ceil(candidateData.length / 2) ? '#4caf50' : '#f44336'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
        )}
      </div>

      <div className="card">
        <h3>Summary metrics</h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
            marginTop: 10,
          }}
        >
          {metrics.map((m) => (
            <div key={m.label} style={{ background: m.bg, border: '1px solid rgba(148, 163, 184, 0.22)', borderRadius: 6, padding: '10px 12px' }}>
              <div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>{m.label}</div>
              <div style={{ fontSize: 30 - Math.max(0, String(m.value).length - 8), fontWeight: 700, color: m.color, lineHeight: 1.1 }}>{m.value}</div>
            </div>
          ))}
        </div>
      </div>

      {missingFields.length > 0 && (
        <RequiredApiNotice
          apis={['Rentcast AVM API', 'Rentcast Property Records API']}
          missingFields={missingFields}
        />
      )}
    </div>
  );
};

export default EquityDashboard;
